import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
} from 'reactstrap';

import { BtnOutlineGreen } from '../../styled-components';
import _ from 'lodash';
import { getValidationErrorMessage } from './helpers';
import ExportCSVRow from './ExportCSVRow';
import contractsAPIs from '../../lib/fetch/contractsAPIs';
import { useAuth } from '../../commons/Auth';

function ContractsImportFeedbackModal({ toggle, isOpen, data = {} }) {
  const [t] = useTranslation('CONTRACTS');
  const [{ newToken } = {}] = useAuth();
  const [totContractValues, setTotContractValues] = useState(0);

  const {
    errors = {},
    insertedContracts = [],
    validationErrors: topValidationErrors,
    updatedContracts = [],
    autonomouslyCreatedCustomersCount = 0,
    autonomouslyCreatedCustomerRows = [],
  } = data;
  let validationErrors = false;
  if (topValidationErrors && Array.isArray(topValidationErrors.message)) {
    validationErrors = topValidationErrors.message;
  }
  if (
    topValidationErrors &&
    topValidationErrors.message &&
    !Array.isArray(topValidationErrors.message)
  ) {
    validationErrors = [topValidationErrors.message];
  }

  useEffect(() => {
    if (
      isOpen &&
      (insertedContracts.length > 0 || updatedContracts.length > 0)
    ) {
      const fetchContracts = async (query) => {
        const { data } = await contractsAPIs.findForTotImport({
          newToken,
          query,
        });
        setTotContractValues(data || 0);
      };

      fetchContracts({
        pod_pdr: [...insertedContracts, ...updatedContracts].map(
          (c) => c.pod_pdr
        ),
      });
    }
  }, [data]);

  const {
    adminsNotFound = [],
    responsibleNotManagingCondo = [],
    condosNotFound = [],
    contractMalformed = [],
    clientsNotFound = [],
    customerExtraNotCreated = [],
    customerExtraOfficeNotFound = [],
    customerExtraCreationFailed = [],
    contractsCondoReservedOffer = [],
    productsNotFound = [],
    customersNotCreated = _.uniqWith(
      [...customerExtraNotCreated, ...contractsCondoReservedOffer],
      _.isEqual
    ),
    nonCompliantProducts = [],
    nonCompliantProductsAdmin = [],
    nonCompliantCustomerAdmin = [],
    contractsNotConnected = [],
    contractsActivationDateTooFar = [],
    alreadyInsertedContracts = [],
    supplierNoLongerManagingContracts = [],
    contractsEndDate = [],
    clientsWithInvalidTaxCode = [],
    agentMalformed = [],
  } = errors;

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        {validationErrors
          ? t('MALFORMED_FILE')
          : t('IMPORTED_CONTRACTS_FEEDBACK')}
      </ModalHeader>

      {!validationErrors && (
        <ModalBody>
          <Row>
            <Col sm="12">
              <h5>
                {t('INSERTED_CONTRACTS_NUMBER')}{' '}
                {insertedContracts?.length || 0}
              </h5>
            </Col>
          </Row>
          <Row>
            <Col sm="12">
              <h5>
                {t('UPDATED_CONTRACTS_NUMBER')}: {updatedContracts?.length || 0}
              </h5>
            </Col>
          </Row>
          <Row>
            <Col sm="12">
              <h5>
                {t('TOTAL_NET_CONTRACTS')}: {totContractValues}
              </h5>
            </Col>
          </Row>
          {autonomouslyCreatedCustomersCount > 0 && (
            <Row>
              <Col sm="12">
                <h5>
                  Clienti creati automaticamente:{' '}
                  {autonomouslyCreatedCustomersCount}
                </h5>
                <ul>
                  <ExportCSVRow
                    data={autonomouslyCreatedCustomerRows}
                    text="Righe contratti usate per creare i clienti"
                  />
                </ul>
              </Col>
            </Row>
          )}
          <Row>
            <Col sm="12">
              <h5>
                {t('NOT_INSERTED_CONTRACTS_NUMBER')}:{' '}
                {_.uniqWith(
                  [
                    ...adminsNotFound,
                    ...responsibleNotManagingCondo,
                    ...condosNotFound,
                    ...contractMalformed,
                    ...clientsNotFound,
                    ...customerExtraNotCreated,
                    ...customerExtraOfficeNotFound,
                    ...customerExtraCreationFailed,
                    ...contractsCondoReservedOffer,
                    ...productsNotFound,
                    ...nonCompliantProducts,
                    ...nonCompliantProductsAdmin,
                    ...nonCompliantCustomerAdmin,
                    ...contractsNotConnected,
                    ...contractsActivationDateTooFar,
                    ...alreadyInsertedContracts,
                    ...supplierNoLongerManagingContracts,
                    ...contractsEndDate,
                    ...clientsWithInvalidTaxCode,
                    ...agentMalformed,
                  ],
                  _.isEqual
                ).length || 0}
              </h5>
              <ul>
                <ExportCSVRow
                  data={adminsNotFound}
                  text={t('ADMINS_NOT_FOUND')}
                />
                <ExportCSVRow
                  data={responsibleNotManagingCondo}
                  text={t('RESPONSIBLE_NOT_MANAGING_CONDO')}
                />
                <ExportCSVRow
                  data={condosNotFound}
                  text={t('CONDOS_NOT_FOUND')}
                />
                <ExportCSVRow
                  data={clientsNotFound}
                  text={t('CLIENTS_NOT_FOUND')}
                />
                <ExportCSVRow
                  data={customerExtraOfficeNotFound}
                  text={t('CUSTOMER_EXTRA_OFFICE_NOT_FOUND')}
                />
                <ExportCSVRow
                  data={customerExtraCreationFailed}
                  text={t('CUSTOMER_EXTRA_CREATION_FAILED')}
                />
                <ExportCSVRow
                  data={customersNotCreated}
                  text={t('CUSTOMERS_NOT_CREATED')}
                >
                  <ul>
                    <ExportCSVRow
                      data={customerExtraNotCreated}
                      text={t('CUSTOMER_EXTRA_NOT_CREATED')}
                    />
                    <ExportCSVRow
                      data={contractsCondoReservedOffer}
                      text={t('CONTRACTS_CONDO_RESERVED_OFFER')}
                    />
                  </ul>
                </ExportCSVRow>
                <ExportCSVRow
                  data={contractMalformed}
                  text={t('MALFORMED_CONTRACTS')}
                />
                <ExportCSVRow
                  data={productsNotFound}
                  text={t('PRODUCTS_NOT_FOUND')}
                />
                <ExportCSVRow
                  data={nonCompliantProducts}
                  text={t('NON_COMPLIANT_CONTRACTS')}
                />
                <ExportCSVRow
                  data={nonCompliantProductsAdmin}
                  text={t('NON_COMPLIANT_CONTRACTS_ADMIN')}
                />
                <ExportCSVRow
                  data={nonCompliantCustomerAdmin}
                  text={t('NOT_COMPLIANT_CUSTOMER_ADMIN')}
                />
                <ExportCSVRow
                  data={contractsNotConnected}
                  text={t('NOT_CONTRACT_TYPE_OF_THIS_SUPPLIER')}
                />
                <ExportCSVRow
                  data={alreadyInsertedContracts}
                  text={t('NOT_OF_THIS_SUPPLIER')}
                />
                <ExportCSVRow
                  data={contractsActivationDateTooFar}
                  text={t('ACTIVATION_DATE_TOO_FAR')}
                />
                <ExportCSVRow
                  data={supplierNoLongerManagingContracts}
                  text={t('SUPPLIER_NO_LONGER_MANAGING')}
                />
                <ExportCSVRow
                  data={contractsEndDate}
                  text={t('CONTRACTS_END_DATE_ERROR')}
                />
                <ExportCSVRow
                  data={clientsWithInvalidTaxCode}
                  text={t('CLIENTS_WITH_INVALID_TAX_CODE')}
                />
                <ExportCSVRow
                  data={agentMalformed}
                  text={t('AGENT_MALFORMED')}
                />
              </ul>
            </Col>
          </Row>
        </ModalBody>
      )}
      {validationErrors && (
        <ModalBody>
          <Row>
            <Col sm="12">
              {validationErrors.map((v) => (
                <h5>{getValidationErrorMessage({ message: v })}</h5>
              ))}
            </Col>
          </Row>
        </ModalBody>
      )}
      <ModalFooter>
        <BtnOutlineGreen type="button" className="uppercase" onClick={toggle}>
          {t('COMMON:CLOSE')}
        </BtnOutlineGreen>
      </ModalFooter>
    </Modal>
  );
}

export default ContractsImportFeedbackModal;
